<template>
  <div class="asset-inventory-container">
    <ds-page-header :title="$t('assetInventory.page.title')" :subtitle="$t('assetInventory.page.subtitle')">
      <template #extra>
        <el-button size="small" icon="el-icon-back" @click="_goBack">{{ $t('assetInventory.cancel') }}</el-button>
        <el-button type="primary" size="small" icon="el-icon-check" @click="_doAssetInventory">
          {{ $t('assetInventory.submit') }}
        </el-button>
      </template>
    </ds-page-header>

    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('assetInventory.title') }}</span>
      </div>
      <el-form :model="assetInventoryInInfo" label-width="120px" class="ds-form">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('assetInventory.name')" prop="name">
              <el-input v-model="assetInventoryInInfo.name" :placeholder="$t('assetInventory.namePlaceholder')" clearable />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('assetInventory.staffName')" prop="staffName">
              <el-input v-model="assetInventoryInInfo.staffName" :placeholder="$t('assetInventory.staffNamePlaceholder')" clearable />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('assetInventory.storehouse')" prop="shId">
              <el-select v-model="assetInventoryInInfo.shId" :placeholder="$t('assetInventory.storehousePlaceholder')"
                style="width:100%" clearable @change="selectResourceStores">
                <el-option v-for="item in assetInventoryInInfo.storehouses" :key="item.shId" :label="item.shName" :value="item.shId" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item :label="$t('assetInventory.invTime')" prop="invTime">
              <el-date-picker v-model="assetInventoryInInfo.invTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('assetInventory.invTimePlaceholder')" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('assetInventory.remark')" prop="remark">
              <el-input v-model="assetInventoryInInfo.remark" type="textarea" :rows="2"
                :placeholder="$t('assetInventory.remarkPlaceholder')" maxlength="250" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <div class="ds-panel">
      <ds-toolbar :show-column-setting="false" :show-density="false" :show-refresh="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('assetInventory.itemInfo') }}</span>
        </template>
        <template #right>
          <el-button v-if="assetInventoryInInfo.index != 2" type="primary" size="small" icon="el-icon-plus"
            @click="_openSelectResourceStoreInfoModel">
            {{ $t('assetInventory.selectItem') }}
          </el-button>
        </template>
      </ds-toolbar>
      <ds-data-table :data="assetInventoryInInfo.resourceStores" border
        :empty-description="$t('assetInventory.page.empty')">
        <el-table-column :label="$t('assetInventory.itemType')" align="center" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.parentRstName || '-' }} &gt; {{ scope.row.rstName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="resName" :label="$t('assetInventory.itemName')" align="center" min-width="140" />
        <el-table-column :label="$t('assetInventory.itemSpec')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.rssName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="resCode" :label="$t('assetInventory.itemCode')" align="center" width="120">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.resCode || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="isFixedName" :label="$t('assetInventory.isFixed')" align="center" width="110" />
        <el-table-column :label="$t('assetInventory.referencePrice')" align="center" min-width="140">
          <template slot-scope="scope">
            <el-select v-model="scope.row.timesId" style="width:100%" @change="_changeTimesId($event, scope.$index)">
              <el-option value="" :label="$t('assetInventory.selectPrice')" />
              <el-option v-for="time in scope.row.times" :key="time.timesId" :label="String(time.price)" :value="time.timesId" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$t('assetInventory.stock')" align="center" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ _getTimesStock(scope.row) }}{{ scope.row.unitCodeName }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('assetInventory.afterQuantity')" align="center" min-width="160">
          <template slot-scope="scope">
            <el-input-number v-model="scope.row.quantity" :placeholder="$t('assetInventory.afterQuantityPlaceholder')"
              style="width:70%" :min="1" :controls="false" />
            <span style="margin-left:5px">{{ scope.row.unitCodeName }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('assetInventory.remark')" align="center" min-width="140">
          <template slot-scope="scope">
            <el-input v-model="scope.row.remark" :placeholder="$t('assetInventory.remarkPlaceholder')" clearable />
          </template>
        </el-table-column>
        <el-table-column :label="$t('assetInventory.operation')" align="center" width="90" fixed="right">
          <template slot-scope="scope">
            <el-button type="danger" size="mini" @click="_removeSelectResourceStoreItem(scope.row.resId)">
              {{ $t('assetInventory.remove') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <choose-resource-store2 ref="chooseResourceStore2" @setSelectResourceStores="handleSetSelectResourceStores" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import ChooseResourceStore2 from '@/components/resource/chooseResourceStore2'
import { saveAssetInventory, listStorehouses } from '@/api/resource/assetInventoryInApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'

export default {
  name: 'AssetInventoryInList',
  components: {
    ChooseResourceStore2,
    DsPageHeader,
    DsToolbar,
    DsDataTable
  },
  data() {
    return {
      assetInventoryInInfo: {
        resourceStores: [],
        shId: '',
        shName: '',
        invTime: '',
        name: '',
        staffName: '',
        storehouses: [],
        remark: '',
        state: '2000',
        communityId: ''
      }
    }
  },
  created() {
    this.assetInventoryInInfo.communityId = getCommunityId()
    this._listShopHouses()
  },
  methods: {
    async _listShopHouses() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.assetInventoryInInfo.communityId
        }
        const { data } = await listStorehouses(params)
        this.assetInventoryInInfo.storehouses = data
      } catch (error) {
        console.error('Failed to fetch storehouses:', error)
      }
    },
    async _doAssetInventory() {
      if (!this.doAssetInventoryValidate()) {
        this.$message.error(this.$t('assetInventory.validateError'))
        return
      }
      if (this.assetInventoryInInfo.resourceStores.length < 1) {
        this.$message.error(this.$t('assetInventory.selectItemError'))
        return
      }
      for (const item of this.assetInventoryInInfo.resourceStores) {
        if (!item.timesId) {
          this.$message.error(this.$t('assetInventory.selectPriceError'))
          return
        }
        if (!item.quantity || parseInt(item.quantity) <= 0) {
          this.$message.error(this.$t('assetInventory.quantityError'))
          return
        }
      }
      try {
        const res = await saveAssetInventory(this.assetInventoryInInfo)
        if (res.code === 0) {
          this.$message.success(res.msg)
          this._goBack()
        } else {
          this.$message.error(res.msg)
        }
      } catch (error) {
        console.error('Failed to save asset inventory:', error)
        this.$message.error(this.$t('assetInventory.saveError'))
      }
    },
    doAssetInventoryValidate() {
      const requiredFields = [
        { field: 'name', message: this.$t('assetInventory.nameRequired') },
        { field: 'staffName', message: this.$t('assetInventory.staffNameRequired') },
        { field: 'shId', message: this.$t('assetInventory.storehouseRequired') },
        { field: 'invTime', message: this.$t('assetInventory.invTimeRequired') },
        { field: 'remark', message: this.$t('assetInventory.remarkRequired') }
      ]
      for (const { field, message } of requiredFields) {
        if (!this.assetInventoryInInfo[field]) {
          this.$message.error(message)
          return false
        }
      }
      return true
    },
    _goBack() {
      this.$router.go(-1)
    },
    _openSelectResourceStoreInfoModel() {
      if (!this.assetInventoryInInfo.shId) {
        this.$message.error(this.$t('assetInventory.selectStorehouseFirst'))
        return
      }
      this.$refs.chooseResourceStore2.open({
        shId: this.assetInventoryInInfo.shId,
        unEditFlag: true
      })
    },
    handleSetSelectResourceStores(resourceStores) {
      const oldList = this.assetInventoryInInfo.resourceStores
      const newList = resourceStores.filter(newItem =>
        !oldList.some(oldItem => oldItem.resId === newItem.resId)
      )
      this.assetInventoryInInfo.resourceStores = [...newList, ...oldList]
    },
    selectResourceStores() {
      this.assetInventoryInInfo.resourceStores = []
      const store = this.assetInventoryInInfo.storehouses.find(
        item => item.shId === this.assetInventoryInInfo.shId
      )
      if (store) {
        this.assetInventoryInInfo.shName = store.shName
      }
    },
    _removeSelectResourceStoreItem(resId) {
      this.assetInventoryInInfo.resourceStores = this.assetInventoryInInfo.resourceStores.filter(
        item => item.resId !== resId
      )
      this.$refs.chooseResourceStore2.removeSelectItem(resId)
    },
    _changeTimesId(value, index) {
      const times = this.assetInventoryInInfo.resourceStores[index].times
      const selectedTime = times.find(item => item.timesId === value)
      if (selectedTime) {
        this.assetInventoryInInfo.resourceStores[index].selectedStock = selectedTime.stock
      }
    },
    _getTimesStock(resourceStore) {
      if (!resourceStore.timesId) return "-"
      const time = resourceStore.times.find(item => item.timesId === resourceStore.timesId)
      return time ? time.stock : 0
    }
  }
}
</script>

<style lang="scss" scoped>
.asset-inventory-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }
  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
.ds-form {
  margin-top: var(--ds-spacing-sm);
}
</style>
